import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, type NavigateOptions } from '@tanstack/react-router';
import { useToast, useToastRefusal, type ToastConfirm } from '@/ds';
import { undoEntries, type BackToSuggest } from '@/features/audit/api';
import { undoOutcomeMessage } from '@/features/audit/vocab';

export interface UndoOffer {
  /** The audit entries the decision recorded; Undo takes each back. */
  entries: (string | null | undefined)[];
  /** Said once the undo landed. */
  undone: string;
  /** Titles the refusal: "Couldn't undo publishing “X”". */
  refused: string;
  /** Titles the refusal when only this entry was refused and the others landed. */
  partial?: { entryId: string; title: string };
  /** Names a refused entry when several were undone together. */
  names?: Map<string, string>;
  /** Where "Open" goes once the undo landed. */
  open?: NavigateOptions;
  /** Asked in a dialog before the undo runs; absent, Undo is one step. */
  confirm?: ToastConfirm;
}

/** The one toast an undo ends with: what it undid and any rule it moved back to Suggest, with Open rule for a single rule. */
export function useUndoOutcomeToast() {
  const toast = useToast();
  const navigate = useNavigate();

  return (done: string | null, backToSuggest: BackToSuggest[], open?: NavigateOptions) => {
    const message = undoOutcomeMessage(done, backToSuggest);
    if (!message) return;
    const [rule] = backToSuggest;
    if (backToSuggest.length === 1 && rule) {
      toast(message, {
        actionLabel: 'Open rule',
        onAction: () =>
          void navigate({ to: '/ops/rules/$ruleId', params: { ruleId: rule.ruleId } }),
      });
    } else {
      toast(
        message,
        open ? { actionLabel: 'Open', onAction: () => void navigate(open) } : undefined,
      );
    }
  };
}

/** A decision's toast whose Undo takes it back through its audit entries; a refusal is quoted where the toast was. */
export function useOfferUndo() {
  const toast = useToast();
  const refuse = useToastRefusal();
  const outcome = useUndoOutcomeToast();
  const queryClient = useQueryClient();

  return (message: string, offer: UndoOffer) => {
    const ids = offer.entries.filter((id): id is string => !!id);
    if (ids.length === 0) {
      toast(message);
      return;
    }

    toast(message, {
      confirm: offer.confirm,
      onAction: () =>
        undoEntries(queryClient, ids).then(({ undone, refused, backToSuggest }) => {
          outcome(
            undone.length && refused.length === 0 ? offer.undone : null,
            backToSuggest,
            offer.open,
          );
          if (refused.length === 1 && ids.length === 1) {
            refuse({ title: offer.refused, reason: refused[0]!.reason });
          } else if (refused.length === 1 && refused[0]!.entryId === offer.partial?.entryId) {
            refuse({ title: offer.partial.title, reason: refused[0]!.reason });
          } else if (refused.length) {
            refuse({
              title: `${offer.refused}: ${refused.length} of ${ids.length} weren't undone`,
              detail: (
                <ul className="rq-toast-refusal-list">
                  {refused.map((r) => (
                    <li key={r.entryId}>
                      {offer.names?.get(r.entryId) ? (
                        <span className="text-body-strong" lang="sr">
                          {offer.names.get(r.entryId)}
                        </span>
                      ) : null}{' '}
                      Reason given: {r.reason}
                    </li>
                  ))}
                </ul>
              ),
            });
          }
        }),
    });
  };
}
